/** A bounded replay recipe, not a serialized World or a copy of its cold journal. */
import { mergeBits, RULES, SAME_ENGINE, validBits } from './sbrules.ts';
export const REPLAY_PROTOCOL = 'city-replay-v1' as const;
/** 重放规矩的版本:和 sbplayer.ts 的 ENGINE_VERSION 一样(测试核对),引擎规矩一变就跟着升。 */
export const REPLAY_ENGINE_VERSION = 'player-v13';
/** 这套规矩来自哪个提交的引擎(integ4 最后一个改引擎规矩的提交 712630e,玩家模式的第 225 位;integ3 是 366eff4,它的回执记了第 19 版规则,照第 20 版的开关读得回;更早 Codex 那版是 f54d7255,player-v11,读不回)。
 * 改了引擎里影响重放的规矩(经济、NPC 的决定)要换成那个提交;只改界面、查询、导出的不换。
 * 回执还记开局时的规则版本和开关(init.rules、init.bits):构建号不一样、引擎版本在 SAME_ENGINE 里、规则版本不比现在新的,照那套开关重放(见 replayBits)。 */
export const REPLAY_BUILD_ID = '712630e705beed6bebfe660154d200792f303fbb';
export const MAX_REPLAY_MUTATIONS = 4096;
export const MAX_REPLAY_BYTES = 768 * 1024;

export interface ReplayCompatibility { engineVersion: string; buildId: string }
export interface ReplayInit {
  seed: number; pop: 80; mode: 'city' | 'player'; playerKind: number | null; enterDay: number; historyEnabled: boolean;
  /** 和邻城通不通商。这个引擎只有 'open';city.enou.org 的收集服务对 f54d7255 以外的版本要求写明。 */
  tradeMode?: 'open';
  /** 开局时的规则版本(sbrules.ts 的 RULES)和置上的开关(0..383,从小到大);没记的旧回执只认同一个构建。 */
  rules?: number; bits?: number[];
}
export type ReplayMutation =
  | { kind: 'pcmd'; order: number; executedDay: number; code: number; target: number; value: number }
  | { kind: 'citizen_act'; order: number; executedDay: number; requestId: number; requestDay: number; actor: number; action: number; target: number; value: number };
export type ReplayMutationInput =
  | { kind: 'pcmd'; executedDay: number; code: number; target: number; value: number }
  | { kind: 'citizen_act'; executedDay: number; requestId: number; requestDay: number; actor: number; action: number; target: number; value: number };
export interface ReplayHistory {
  enabled: boolean; included: false;
  archiveState: 'unavailable' | 'off' | 'opening' | 'ready' | 'blocked' | 'memory';
  confirmedThroughDay: number | null; coverageStart: number | null; retainedOnly: boolean | null; durable: boolean;
}
export interface ReplayReceipt {
  v: 1; protocol: typeof REPLAY_PROTOCOL; engineVersion: string; buildId: string;
  init: ReplayInit; completeDay: number; mutations: ReplayMutation[]; history: ReplayHistory;
  print?: { day: number; value: string };
}
type Obj = Record<string, unknown>;
const fail = (message: string): never => { throw new Error(`重放回执无效：${message}`); };
const object = (x: unknown, label: string): Obj => {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return fail(label);
  return x as Obj;
};
const keys = (x: Obj, required: string[], optional: string[] = []) => {
  if (required.some(k => !Object.hasOwn(x, k)) || Object.keys(x).some(k => !required.includes(k) && !optional.includes(k))) fail('字段不完整或包含未知字段');
};
const uint = (x: unknown): x is number => typeof x === 'number' && Number.isSafeInteger(x) && x >= 0 && x <= 0xffffffff;
const number = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
function initOf(value: unknown): ReplayInit {
  const x = object(value, '开局参数'); keys(x, ['seed', 'pop', 'mode', 'playerKind', 'enterDay', 'historyEnabled'], ['tradeMode', 'rules', 'bits']);
  if (Object.hasOwn(x, 'tradeMode') && x.tradeMode !== 'open') return fail('贸易规则');
  if (Object.hasOwn(x, 'rules') && (!uint(x.rules) || x.rules > 0xffff)) return fail('规则版本');
  if (Object.hasOwn(x, 'bits') && !validBits(x.bits)) return fail('开关');
  if (!uint(x.seed) || x.pop !== 80 || !uint(x.enterDay) || typeof x.historyEnabled !== 'boolean') return fail('开局参数');
  if (x.mode === 'city' ? x.playerKind !== null || x.enterDay !== 0 : x.mode !== 'player' || !uint(x.playerKind) || x.playerKind > 3) return fail('身份或进城日');
  return { seed: x.seed, pop: 80, mode: x.mode as 'city' | 'player', playerKind: x.playerKind as number | null, enterDay: x.enterDay, historyEnabled: x.historyEnabled, ...(Object.hasOwn(x, 'tradeMode') ? { tradeMode: 'open' as const } : {}),
    ...(Object.hasOwn(x, 'rules') ? { rules: x.rules as number } : {}), ...(Object.hasOwn(x, 'bits') ? { bits: [...x.bits as number[]] } : {}) };
}
function mutationOf(value: unknown): ReplayMutation {
  const x = object(value, '指令');
  if (!uint(x.order) || !uint(x.executedDay)) return fail('指令顺序或执行日');
  if (x.kind === 'pcmd') {
    keys(x, ['kind', 'order', 'executedDay', 'code', 'target', 'value']);
    if (!uint(x.code) || !uint(x.target) || !uint(x.value)) return fail('玩家指令参数');
    return { kind: 'pcmd', order: x.order, executedDay: x.executedDay, code: x.code, target: x.target, value: x.value };
  }
  keys(x, ['kind', 'order', 'executedDay', 'requestId', 'requestDay', 'actor', 'action', 'target', 'value']);
  if (x.kind !== 'citizen_act' || !uint(x.requestId) || !uint(x.requestDay) || !uint(x.actor) || !uint(x.action) || !uint(x.target) || !number(x.value)) return fail('居民行动参数');
  return { kind: 'citizen_act', order: x.order, executedDay: x.executedDay, requestId: x.requestId, requestDay: x.requestDay, actor: x.actor, action: x.action, target: x.target, value: x.value };
}
function historyOf(value: unknown, init: ReplayInit, day: number): ReplayHistory {
  const x = object(value, '历史覆盖'); keys(x, ['enabled', 'included', 'archiveState', 'confirmedThroughDay', 'coverageStart', 'retainedOnly', 'durable']);
  // The original preference and current coverage differ when history was enabled during play.
  if (typeof x.enabled !== 'boolean' || x.included !== false || !['unavailable', 'off', 'opening', 'ready', 'blocked', 'memory'].includes(String(x.archiveState)) || typeof x.durable !== 'boolean') return fail('历史覆盖');
  for (const k of ['confirmedThroughDay', 'coverageStart']) if (x[k] !== null && (!uint(x[k]) || (x[k] as number) > day)) fail('历史覆盖日期');
  if (x.retainedOnly !== null && typeof x.retainedOnly !== 'boolean') return fail('历史覆盖范围');
  return { enabled: x.enabled, included: false, archiveState: x.archiveState as ReplayHistory['archiveState'], confirmedThroughDay: x.confirmedThroughDay as number | null, coverageStart: x.coverageStart as number | null, retainedOnly: x.retainedOnly as boolean | null, durable: x.durable };
}
/** 这一份回执这一版读不读得回:同一个构建的照读;不同构建的,要引擎版本在 SAME_ENGINE 里、记了规则版本、不比现在新(和玩家存档一个规矩)。 */
export const replayCompatible = (engineVersion: unknown, buildId: unknown, init: ReplayInit, expected: ReplayCompatibility) =>
  (engineVersion === expected.engineVersion && buildId === expected.buildId)
  || (typeof engineVersion === 'string' && SAME_ENGINE.includes(engineVersion) && SAME_ENGINE.includes(expected.engineVersion) && init.rules !== undefined && init.rules <= RULES);
/** 照这份回执重放要置上的开关:记下的,加上它开局以后才加的新规则。没记规则版本的(同一个构建)照记下的。 */
export const replayBits = (init: ReplayInit) => init.rules === undefined ? [...init.bits ?? []] : mergeBits(init.bits, init.rules);
/** Validates and clones JSON data. expected is mandatory; a receipt cannot select its own compatible build. */
export function validateReplayReceipt(value: unknown, expected: ReplayCompatibility): ReplayReceipt {
  if (!expected || !/^[0-9a-f]{40}$/.test(expected.buildId) || typeof expected.engineVersion !== 'string' || !expected.engineVersion) return fail('当前构建标识');
  const x = object(value, '回执'); keys(x, ['v', 'protocol', 'engineVersion', 'buildId', 'init', 'completeDay', 'mutations', 'history'], ['print']);
  if (x.v !== 1 || x.protocol !== REPLAY_PROTOCOL || typeof x.engineVersion !== 'string' || typeof x.buildId !== 'string' || !/^[0-9a-f]{40}$/.test(x.buildId)) return fail('引擎、协议或构建不兼容');
  const init = initOf(x.init);
  if (!replayCompatible(x.engineVersion, x.buildId, init, expected)) return fail('引擎、协议或构建不兼容');
  if (!uint(x.completeDay) || x.completeDay < init.enterDay || !Array.isArray(x.mutations) || x.mutations.length > MAX_REPLAY_MUTATIONS) return fail('完整日或指令数量');
  let previous = 0;
  const mutations = x.mutations.map((raw, i) => {
    const m = mutationOf(raw);
    if (m.order !== i || m.executedDay < previous || m.executedDay > (x.completeDay as number) || m.executedDay < init.enterDay || (m.kind === 'pcmd' && init.mode !== 'player')) fail('指令顺序、日期或模式');
    previous = m.executedDay; return m;
  });
  // 记的是回执自己的构建(读回别的构建的回执时不冒充成这一版的)
  const result: ReplayReceipt = { v: 1, protocol: REPLAY_PROTOCOL, engineVersion: x.engineVersion, buildId: x.buildId, init, completeDay: x.completeDay, mutations, history: historyOf(x.history, init, x.completeDay) };
  if (Object.hasOwn(x, 'print')) {
    const p = object(x.print, '指纹'); keys(p, ['day', 'value']);
    if (p.day !== x.completeDay || typeof p.value !== 'string' || !/^[0-9a-f]{16}$/.test(p.value)) return fail('完整日指纹');
    result.print = { day: x.completeDay, value: p.value };
  }
  if (new TextEncoder().encode(JSON.stringify(result)).length > MAX_REPLAY_BYTES) fail('回执超过保存大小上限');
  return result;
}

/** Bounded tracking never prevents an actual game command. Overflow makes new checkpoints unavailable; it never truncates a valid recipe. */
export class ReplayRecorder {
  private readonly init: ReplayInit;
  private readonly expected: ReplayCompatibility;
  private readonly mutations: ReplayMutation[] = [];
  private unavailable: string | null = null;
  constructor(init: ReplayInit, expected: ReplayCompatibility) { this.init = initOf(init); this.expected = { ...expected }; }
  record(input: ReplayMutationInput): boolean {
    if (this.unavailable) return false;
    if (this.mutations.length >= MAX_REPLAY_MUTATIONS) { this.unavailable = '指令数量达到保存上限，较早的已保存进度仍可使用'; return false; }
    const m = mutationOf({ ...input, order: this.mutations.length });
    const previous = this.mutations.at(-1);
    if (previous && m.executedDay < previous.executedDay) { this.unavailable = '执行日期倒退'; return false; }
    this.mutations.push(m); return true;
  }
  snapshot(completeDay: number, print: ReplayReceipt['print'], history: ReplayHistory): ReplayReceipt {
    if (this.unavailable) throw new Error(this.unavailable);
    return validateReplayReceipt({ v: 1, protocol: REPLAY_PROTOCOL, ...this.expected, init: this.init, completeDay, mutations: this.mutations, history, ...(print ? { print } : {}) }, this.expected);
  }
}

/** Executes mutations in their original within-day order, before the caller starts the next day. */
export function replayMutationsAtDay(receipt: ReplayReceipt, next: number, day: number, invoke: (mutation: ReplayMutation) => void): number {
  if (!uint(next) || next > receipt.mutations.length || !uint(day)) return fail('重放游标');
  while (next < receipt.mutations.length) {
    const m = receipt.mutations[next];
    if (m.executedDay < day) return fail('重放错过执行日');
    if (m.executedDay > day) break;
    invoke(m); next++;
  }
  return next;
}
